"use client";

import { useEffect } from "react";

import { reportError } from "@/lib/report-error";
import { cn } from "@/lib/utils";

import { ErrorState } from "@/components/states/error-state";

export interface RouteErrorBoundaryProps {
  /** The error thrown in the segment subtree (Next.js `error.tsx` prop). */
  error: Error & { digest?: string };
  /** Re-render the segment (Next.js `error.tsx` prop). */
  reset: () => void;
  /** Reporting tag, e.g. "boundary:(app)" — funnels into `reportError`. */
  source: string;
  title?: string;
  description?: string;
  /** Wrapper classes; defaults keep the boundary centered inside its shell. */
  className?: string;
}

/**
 * Shared body for route-group `error.tsx` boundaries (ENGINEERING.md §5). Each
 * group's `error.tsx` is a thin `"use client"` wrapper that renders this with a
 * distinct `source` tag, so a failure in one segment keeps the surrounding
 * layout chrome (header/sidebar) intact and reports through the one funnel.
 */
export function RouteErrorBoundary({
  error,
  reset,
  source,
  title = "Something went wrong",
  description = "An unexpected error occurred while loading this page. You can try again — if it keeps happening, the team has been notified.",
  className,
}: RouteErrorBoundaryProps) {
  useEffect(() => {
    reportError(error, { source, digest: error.digest });
  }, [error, source]);

  return (
    <div
      className={cn(
        "mx-auto flex min-h-[50vh] w-full max-w-2xl items-center justify-center p-6",
        className,
      )}
    >
      <ErrorState
        title={title}
        description={description}
        onRetry={reset}
        retryLabel="Try again"
      />
    </div>
  );
}
